import React from 'react'
import { I18n } from 'react-redux-i18n'
import { connect } from 'react-redux'
import _ from 'lodash'
import { priceFormat } from '../../utils/currency'
import { countPax } from '../../utils/serviceTxn'

const CheckInTypeResult = (props) => {
  // Show result if checkInType is set OR extraPrivileges is set
  if (!props.checkInType && !props.extraPrivileges) return null
  return renderCheckInResult(props)
}

const mapStateToProps = ({ checkin }) => ({
  ...checkin.serviceTxn,
  hasReservation:
    checkin.serviceTxn.reservation &&
    checkin.serviceTxn.reservation.confirmationNumber,
  guestOf: checkin.serviceTxn.guestOf,
})

export default connect(mapStateToProps)(CheckInTypeResult)

const renderCheckInResult = ({
  checkInType,
  airlineCheckInInfo,
  agentCheckInInfo,
  walkinCheckInInfo,
  serviceInfo,
  hasReservation,
  onShowReservationInfo,
  extraPrivileges,
  guestOf,
}) => {
  // If no checkInType but has extraPrivileges (voucher/complimentary without base check-in)
  if (!checkInType && extraPrivileges) {
    return (
      <ExtraPrivilegeOnlyResult
        extraPrivileges={extraPrivileges}
        guestOf={guestOf}
        serviceInfo={serviceInfo}
        hasReservation={hasReservation}
        onShowReservationInfo={onShowReservationInfo}
      />
    )
  }

  switch (checkInType) {
    case 'airline':
      return (
        <AirlineCheckInResult
          info={airlineCheckInInfo}
          serviceInfo={serviceInfo}
          hasReservation={hasReservation}
          onShowReservationInfo={onShowReservationInfo}
          guestOf={guestOf}
        />
      )
    case 'agent':
      return (
        <AgentCheckInResult
          info={agentCheckInInfo}
          serviceInfo={serviceInfo}
          hasReservation={hasReservation}
          onShowReservationInfo={onShowReservationInfo}
          guestOf={guestOf}
        />
      )
    case 'walkin':
      return (
        <WalkinCheckInResult
          info={walkinCheckInInfo}
          serviceInfo={serviceInfo}
          hasReservation={hasReservation}
          onShowReservationInfo={onShowReservationInfo}
          guestOf={guestOf}
        />
      )
    default:
      return null
  }
}

const AirlineCheckInResult = ({
  info: { airline },
  serviceInfo,
  hasReservation,
  onShowReservationInfo,
  guestOf,
}) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
    <div className="flex items-center justify-between gap-4">
      {/* Left: Airline Info */}
      <div className="flex items-center gap-4 flex-1">
        {/* Airline Logo */}
        <div className="flex-shrink-0">
          {airline.logo ? (
            <div className="w-16 h-16 flex items-center justify-center">
              <img
                src={airline.logo}
                alt={airline.name}
                title={airline.name}
                className="max-w-full max-h-full object-contain"
              />
            </div>
          ) : (
            <div className="w-16 h-16 rounded-lg bg-primary-700 flex items-center justify-center text-white font-bold text-lg">
              {airline.code}
            </div>
          )}
        </div>

        {/* Airline Name and Duration */}
        <div className="flex-1 min-w-0">
          <h3 className="text-gray-900 font-semibold text-lg truncate">
            {airline.name}
          </h3>
          <p className="text-gray-500 text-sm">{airline.code}</p>
          {renderBadgesRow(guestOf, serviceInfo)}
        </div>
      </div>

      {/* Right: Action Buttons */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {/* Product Price Icon Badge */}
        {_.get(serviceInfo, 'productPrice.pricingProduct') && (
          <div
            className="w-10 h-10 bg-gray-900 rounded-lg flex items-center justify-center text-white"
            title="Has Product Price"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
        )}

        {/* Reservation Info Button */}
        {hasReservation && onShowReservationInfo && (
          <button
            onClick={onShowReservationInfo}
            className="w-10 h-10 bg-primary-100 hover:bg-primary-200 rounded-lg flex items-center justify-center text-primary-700 transition-colors"
            title="View Reservation Info"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  </div>
)

const AgentCheckInResult = ({
  info: { agent },
  serviceInfo,
  hasReservation,
  onShowReservationInfo,
  guestOf,
}) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
    <div className="flex items-center justify-between gap-4">
      {/* Left: Agent Info */}
      <div className="flex items-center gap-4 flex-1">
        {/* Agent Icon */}
        <div className="w-16 h-16 rounded-lg bg-primary-700 flex items-center justify-center text-white flex-shrink-0">
          <svg
            className="w-8 h-8"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>

        {/* Agent Name and Duration */}
        <div className="flex-1 min-w-0">
          <h3 className="text-gray-900 font-semibold text-lg truncate">
            {agent.name}
          </h3>
          <p className="text-gray-500 text-sm">Agent</p>
          {renderBadgesRow(guestOf, serviceInfo)}
        </div>
      </div>

      {/* Right: Reservation Info Button */}
      {hasReservation && onShowReservationInfo && (
        <button
          onClick={onShowReservationInfo}
          className="w-10 h-10 bg-primary-100 hover:bg-primary-200 rounded-lg flex items-center justify-center text-primary-700 transition-colors flex-shrink-0"
          title="View Reservation Info"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </button>
      )}
    </div>
  </div>
)

const WalkinCheckInResult = ({
  info: { type, card },
  serviceInfo,
  hasReservation,
  onShowReservationInfo,
  guestOf,
}) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
    <div className="flex items-center justify-between gap-4">
      {/* Left: Payment Info */}
      <div className="flex items-center gap-4 flex-1">
        {/* Payment Icon */}
        <div className="w-16 h-16 rounded-lg bg-primary-700 flex items-center justify-center text-white flex-shrink-0">
          {type === 'cash' && (
            <svg
              className="w-8 h-8"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
          )}
          {type === 'creditCard' && (
            <svg
              className="w-8 h-8"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
              />
            </svg>
          )}
          {type === 'card' && (
            <svg
              className="w-8 h-8"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2"
              />
            </svg>
          )}
          {type !== 'cash' && type !== 'creditCard' && type !== 'card' && (
            <svg
              className="w-8 h-8"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
          )}
        </div>

        {/* Payment Details */}
        <div className="flex-1 min-w-0">
          {renderWalkinCheckInResult(type, card, serviceInfo, guestOf)}
        </div>
      </div>

      {/* Right: Action Buttons */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {/* Product Price Icon Badge */}
        {_.get(serviceInfo, 'productPrice.pricingProduct') && (
          <div
            className="w-10 h-10 bg-gray-900 rounded-lg flex items-center justify-center text-white"
            title="Has Product Price"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
        )}

        {/* Reservation Info Button */}
        {hasReservation && onShowReservationInfo && (
          <button
            onClick={onShowReservationInfo}
            className="w-10 h-10 bg-primary-100 hover:bg-primary-200 rounded-lg flex items-center justify-center text-primary-700 transition-colors"
            title="View Reservation Info"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  </div>
)

const renderWalkinCheckInResult = (type, card, serviceInfo, guestOf) => {
  if (type === 'card')
    return (
      <div>
        <h3 className="text-gray-900 font-semibold text-lg truncate">
          {card.name}
        </h3>
        <p className="text-gray-500 text-sm">
          {I18n.t(`checkInScene.field.${type}`)}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-1">
          {card && card.privilegeClass && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
              {card.privilegeClass}
            </span>
          )}
          {renderGuestOfBadge(guestOf)}
          {renderDuration(serviceInfo)}
        </div>
      </div>
    )
  return (
    <div>
      <h3 className="text-gray-900 font-semibold text-lg">
        {I18n.t(`checkInScene.field.${type}`)}
      </h3>
      {renderWalkinPrice(type, serviceInfo)}
      {renderBadgesRow(guestOf, serviceInfo)}
    </div>
  )
}

const renderWalkinPrice = (type, serviceInfo) => {
  switch (type) {
    case 'cash':
    case 'creditCard':
      const productPrice = _.get(serviceInfo, 'productPrice')
      if (_.isNil(productPrice)) return null
      return renderProductPrice(productPrice, serviceInfo)
    default:
      return null
  }
}

const renderProductPrice = (productPrice, serviceInfo) => {
  const countPaxAmount = countPax(serviceInfo)
  return (
    <div className="mt-1">
      <p className="text-primary-600 font-semibold text-base">
        {priceFormat(productPrice.price * countPaxAmount)}
      </p>
      <p className="text-gray-500 text-xs">
        {priceFormat(productPrice.price)} x {countPaxAmount} pax
      </p>
    </div>
  )
}

const ExtraPrivilegeOnlyResult = ({
  extraPrivileges,
  guestOf,
  serviceInfo,
  hasReservation,
  onShowReservationInfo,
}) => {
  const getPrivilegeDisplay = () => {
    switch (extraPrivileges) {
      case 'voucher':
        return { name: 'Voucher', icon: 'ticket' }
      case 'complimentary':
        return { name: 'Complimentary', icon: 'gift' }
      case 'guest':
        return { name: 'Guest Privilege', icon: 'user-group' }
      default:
        return { name: extraPrivileges, icon: 'ticket' }
    }
  }

  const privilege = getPrivilegeDisplay()

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
      <div className="flex items-center justify-between gap-4">
        {/* Left: Privilege Info */}
        <div className="flex items-center gap-4 flex-1">
          {/* Privilege Icon */}
          <div className="w-16 h-16 rounded-lg bg-primary-700 flex items-center justify-center text-white flex-shrink-0">
            {privilege.icon === 'ticket' && (
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
                />
              </svg>
            )}
            {privilege.icon === 'gift' && (
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
                />
              </svg>
            )}
            {privilege.icon === 'user-group' && (
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            )}
          </div>

          {/* Privilege Name and Duration */}
          <div className="flex-1 min-w-0">
            <h3 className="text-gray-900 font-semibold text-lg">
              {privilege.name}
            </h3>
            <p className="text-gray-500 text-sm">Extra Privilege</p>
            {renderBadgesRow(guestOf, serviceInfo)}
          </div>
        </div>

        {/* Right: Reservation Info Button */}
        {hasReservation && onShowReservationInfo && (
          <button
            onClick={onShowReservationInfo}
            className="w-10 h-10 bg-primary-100 hover:bg-primary-200 rounded-lg flex items-center justify-center text-primary-700 transition-colors flex-shrink-0"
            title="View Reservation Info"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  )
}

const renderGuestOfBadge = (guestOf) => {
  if (!guestOf) return null
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
      Guest of: {_.get(guestOf, 'customerName', '-')}
    </span>
  )
}

const renderBadgesRow = (guestOf, serviceInfo) => {
  const guestBadge = renderGuestOfBadge(guestOf)
  const durationBadge = renderDuration(serviceInfo)
  if (!guestBadge && !durationBadge) return null
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1">
      {guestBadge}
      {durationBadge}
    </div>
  )
}

const renderDuration = (serviceInfo) => {
  if (!serviceInfo || serviceInfo.duration === undefined) {
    return null
  }

  if (serviceInfo.duration > 0) {
    return (
      <div className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-primary-50 text-primary-700 font-medium text-xs">
        <svg
          className="w-3.5 h-3.5 mr-1"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        {serviceInfo.duration} hr.
      </div>
    )
  } else {
    return (
      <div className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-green-50 text-green-700 font-medium text-xs">
        <svg
          className="w-3.5 h-3.5 mr-1"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        </svg>
        Pass
      </div>
    )
  }
}
